import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GenericHttpService } from '@core/services';
import { User } from '@core/shared/user';
import { environment } from '@environments/environment';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import { SignInResult, Token } from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends GenericHttpService<User | SignInResult> {
  private readonly USER_ITEM = '_user';
  private readonly TOKEN_ITEM = '_token';
  private readonly THERAPIST_VERIFIED = '_therapistVerified';


  private userSubject = new BehaviorSubject<User>(this._getUser());
  private tokenSubject = new BehaviorSubject<Token>(this._getToken());

  isLoggedIn = new BehaviorSubject<boolean>(this.loggedIn);

  isAdmin = new BehaviorSubject<boolean>(this.isAdminUser);

  constructor(
    httpClient: HttpClient,
    ) {
    super(httpClient, environment.apiUrl, 'auth');
  }

  get userValue(): User {
    return this.userSubject?.value;
  }

  get tokenValue() {
    return this.tokenSubject?.value;
  }


  private get loggedIn(): boolean {
    const user = this.userSubject?.value;
    const token = this.tokenSubject?.value;
    return !!(user && token);
  }

  private get isAdminUser(): boolean {
    const user: any = this.userSubject?.value;
    return user?.role === 'admin';
  }

  signIn( email, password, otp ): Observable<SignInResult> {
    return super.post({ email, password, otp }, 'login').pipe(
      map((response: any) => {
        if ( response.status === 200 ) {
          const result: SignInResult = response.body;
          const { user, token } = result;
          this._saveUser(user);
          this._saveToken(token);
          this.isLoggedIn.next(true);
          this.isAdmin.next(true);
          return result;
        }
      })
    );
  }
  sendOtpOverEmail(email): Observable<any>  {
    super.endpoint = 'auth';
    return super.post({ email }, 'sendOtpVerificationEmail').pipe(
      map((response: any) => {
        if ( response.status === 200 ) {
          return response.body;
        }
        throw new Error('OTP sending failed');
      }),
    catchError((err) => {
      console.error('Error in OTP sending:', err);
      return throwError(err);
    })
    );
  }

  resendOtpOverEmail(email): Observable<any>  {
    return super.post({ email }, 'resendOtpVerificationEmail').pipe(
      map((response: any) => {
        if ( response.status === 200 ) {
          return response.body;
        }
        throw new Error('OTP sending failed');
      }),
      catchError((err) => {
        console.error('Error in OTP sending:', err);
        return throwError(err);
      })
    );
  }


  logout() {
    super.endpoint = 'auth';
    return super.get('logout');
  }

  logoutLocal() {
    localStorage.removeItem(this.USER_ITEM);
    localStorage.removeItem(this.TOKEN_ITEM);
    localStorage.removeItem(this.THERAPIST_VERIFIED);
    this.userSubject.next(null);
    this.tokenSubject.next(null);
    this.isLoggedIn.next(false);
  }

  private _getUser(): User {
    return JSON.parse(localStorage.getItem(this.USER_ITEM)) as User;
  }

  private _getToken(): Token {
    return JSON.parse(localStorage.getItem(this.TOKEN_ITEM)) as Token;
  }

  private _saveUser(user: User): void {
    localStorage.setItem(this.USER_ITEM, JSON.stringify(user));
    this.userSubject.next(user);
  }

  private _saveToken(token: Token): void {
    localStorage.setItem(this.TOKEN_ITEM, JSON.stringify(token));
    this.tokenSubject.next(token);
  }

  // Activate account

  // Personal Detail save
  activateAccount(token) {
    super.endpoint = 'auth';
    return super.post(token , 'register');
  }

  // Sign up form
  signup(signupDetails) {
    super.endpoint = 'auth';
    return super.post(signupDetails , 'signup');
  }

  // Send token reset password
  sendPasswordResetToken(emailInfo) {
    super.endpoint = 'auth';
    return super.post(emailInfo, 'send-password-reset');
  }

  // Reset password
  resetPassword(updatedInfo) {
    super.endpoint = 'auth';
    return super.post(updatedInfo, 'reset-password');
  }

  getUserValue(): User {
    return JSON.parse(localStorage.getItem(this.USER_ITEM)) as User;
  }

  getRefreshToken(tokenParams: any): Observable<any> {
    return super.post( tokenParams, 'refreshAccessToken').pipe(
      tap(response => console.log('Received refresh token response:')),
      catchError(error => {
        console.error('Error during refresh token API call:', error);
        return throwError(error);
      })
    );
  }
}
